import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { config } from '../../config';

export default function GestionProduits() {
    const [produits, setProduits] = useState([]);
    const [loading, setLoading] = useState(true);
    const [nom, setNom] = useState('');
    const [quantite, setQuantite] = useState('');
    const [submitting, setSubmitting] = useState(false);

    // Utilisation de la configuration centralisée
    const API_BASE = config.apiBase;

    const fetchProduits = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/financier/get_produits.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setProduits(data.produits);
            }
        } catch (error) {
            toast.error('Erreur de chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchProduits();
    }, []);

    const ajouterProduit = async () => {
        if (!nom.trim()) {
            toast.error('Nom du produit requis');
            return;
        }
        if (!quantite || parseInt(quantite) <= 0) {
            toast.error('Quantité valide requise');
            return;
        }

        setSubmitting(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/financier/ajouter_produit.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    nom: nom,
                    quantite: parseInt(quantite)
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Produit ajouté');
                setNom('');
                setQuantite('');
                fetchProduits();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        } finally {
            setSubmitting(false);
        }
    };

    const approvisionner = async (id, stockActuel) => {
        const quantiteAjout = prompt(`Stock actuel: ${stockActuel}\nQuantité à ajouter:`);
        if (!quantiteAjout || parseInt(quantiteAjout) <= 0) return;
        
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_BASE}/financier/approvisionner.php`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    id_produit: id,
                    quantite: parseInt(quantiteAjout)
                })
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Stock mis à jour');
                fetchProduits();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            toast.error('Erreur');
        }
    };

    if (loading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-emerald-200 border-t-emerald-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-6">
            <div className="mx-auto max-w-5xl">
                {/* Header */}
                <div className="mb-8 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 p-6 text-white shadow-xl">
                    <h1 className="text-2xl font-bold">💰 Gestion des produits</h1>
                    <p className="mt-2 opacity-90">Ajoutez et gérez les produits pour l'infirmerie</p>
                </div>

                {/* Formulaire ajout produit */}
                <div className="mb-6 rounded-2xl bg-white p-6 shadow-xl">
                    <h2 className="text-lg font-bold text-slate-800 mb-4">➕ Nouveau produit</h2>
                    <div className="flex flex-col sm:flex-row gap-4">
                        <input
                            type="text"
                            placeholder="Nom du produit"
                            value={nom}
                            onChange={(e) => setNom(e.target.value)}
                            className="flex-1 rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-emerald-400"
                        />
                        <input
                            type="number"
                            placeholder="Quantité initiale"
                            value={quantite}
                            onChange={(e) => setQuantite(e.target.value)}
                            className="w-32 rounded-xl border border-slate-200 px-4 py-2 outline-none focus:border-emerald-400"
                        />
                        <button
                            onClick={ajouterProduit}
                            disabled={submitting}
                            className="bg-emerald-600 text-white px-6 py-2 rounded-xl hover:bg-emerald-700 disabled:opacity-50"
                        >
                            {submitting ? 'Ajout...' : 'Ajouter'}
                        </button>
                    </div>
                </div>

                {/* Liste des produits */}
                {produits.length === 0 ? (
                    <div className="rounded-2xl bg-white p-12 text-center">
                        <p className="text-slate-500">Aucun produit disponible</p>
                    </div>
                ) : (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {produits.map((p) => (
                            <div key={p.id_produit} className="rounded-2xl bg-white p-5 shadow-lg">
                                <div className="flex justify-between items-start mb-2">
                                    <h3 className="font-bold text-slate-800 text-lg">{p.nom}</h3>
                                    {p.quantite <= p.seuil_alerte && p.quantite > 0 && (
                                        <span className="text-xs bg-orange-100 text-orange-600 px-2 py-1 rounded-full">⚠️ Stock bas</span>
                                    )}
                                    {p.quantite === 0 && (
                                        <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">📦 Rupture</span>
                                    )}
                                </div>
                                <div className="mb-3">
                                    <p className="text-3xl font-bold text-emerald-600">{p.quantite}</p>
                                    <p className="text-sm text-slate-500">unités</p>
                                </div>
                                <button
                                    onClick={() => approvisionner(p.id_produit, p.quantite)}
                                    className="w-full bg-blue-600 text-white py-2 rounded-xl hover:bg-blue-700 transition"
                                >
                                    📦 Approvisionner
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}